import { Injectable } from '@angular/core';
import {
  TREQUEST_ADD_REVISION,
  TREQUEST_GET_LOCATION_SHIFTS,
  TREQUEST_COMPLETE_MAINTENANCE_TICKET,
  TREQUEST_CREATE_MAINTENANCE_TICKET,
  TREQUEST_CREATE_TICKET,
  TREQUEST_GET_ALL_EMPLOYEE_TICKETS,
  TREQUEST_GET_ALL_MAINTENANCE_TICKETS,
  TREQUEST_GET_MACHINE_TICKET_HISTORY,
  TREQUEST_GET_SHIFT_DETAILS,
  TREQUEST_REASSIGN_MECHANIC,
  TREQUEST_START_MAINTENANCE_TICKET,
  TREQUEST_UPDATE_ISSUE,
  TREQUEST_UPDATE_REVISION,
  TShiftRevision,
  TREQUEST_REASSIGN_MAINTENANCE_MECHANIC,
  TTicketId,
  TMaintenanceTicketStatus,
  TREQUEST_RAISE_SPAREPART_REQUEST,
  TREQUEST_GET_SPAREPART_REQUEST_BY_SOURCE,
  TRaiseARequestId,
  TMachineIssueTicketsFetchReason,
  TMaintenanceTicketsFetchReason,
  TRESPONSE_GET_SPAREPART_REQUEST_BY_SOURCE,
  TREQUEST_MARK_CHECKLIST_COMPLETED,
  TREQUEST_RAISE_NEEDLE_REQUEST,
  TFilterSelected,
  TUserPaginationOption,
  TREQUEST_GET_ALL_BRANDS_AND_SPECIFICATIONS,
  TM8MachineId,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { TMachineMaintenanceState } from '../reducers';
import * as TicketManagementActions from './ticket-management.actions';
import * as TicketManagementSelectors from './ticket-management.selectors';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { from, lastValueFrom, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TicketManagementFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private mmDataService: MachineMaintenanceDataService,
  ) {}

  createNewTicket(payload: Omit<TREQUEST_CREATE_TICKET, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.CreateNewTicket(payload));
  }

  assignTicketToMe(payload: Omit<TREQUEST_CREATE_TICKET, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.AssignTicketToMe(payload));
  }

  updateIssue(payload: Omit<TREQUEST_UPDATE_ISSUE, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.UpdateIssue(payload));
  }

  reAssignMechanic(payload: Omit<TREQUEST_REASSIGN_MECHANIC, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.ReAssignMechanic(payload));
  }

  reIssueTicket(ticketId: TTicketId) {
    this.store.dispatch(new TicketManagementActions.ReIssueTicket(ticketId));
  }

  approveTicket(ticketId: TTicketId) {
    this.store.dispatch(new TicketManagementActions.ApproveTicket(ticketId));
  }

  pickupTicket(ticketId: TTicketId) {
    this.store.dispatch(new TicketManagementActions.PickupTicket(ticketId));
  }

  markTicketAsCompleted(ticketId: TTicketId, isActiveMachine: boolean) {
    this.store.dispatch(new TicketManagementActions.MarkTicketAsCompleted(ticketId, isActiveMachine));
  }

  resetEmployeeTickets() {
    this.store.dispatch(new TicketManagementActions.ResetEmployeeTickets());
  }

  fetchAllEmployeeTickets(
    payload: Omit<TREQUEST_GET_ALL_EMPLOYEE_TICKETS, 'factoryId'>,
    fetchReason: TMachineIssueTicketsFetchReason,
  ) {
    this.store.dispatch(new TicketManagementActions.GetAllEmployeeTickets(payload, fetchReason));
  }

  fetchEmployeeTicketDetails(ticketId: TTicketId) {
    this.store.dispatch(new TicketManagementActions.GetEmployeeTicketDetails(ticketId));
  }

  fetchMachineTicketHistory(payload: Omit<TREQUEST_GET_MACHINE_TICKET_HISTORY, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.GetMachineTicketHistory(payload));
  }

  fetchMachineMaintenanceHistory(payload: Omit<TREQUEST_GET_MACHINE_TICKET_HISTORY, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.GetMachineMaintenanceHistory(payload));
  }

  fetchEmployeeTicketSummary(selectedFilters: TFilterSelected[]) {
    this.store.dispatch(new TicketManagementActions.GetEmployeeTicketSummary(selectedFilters));
  }

  fetchTicketSummaryAnalytics(selectedFilters: TFilterSelected[]) {
    this.store.dispatch(new TicketManagementActions.GetTicketSummaryAnalytics(selectedFilters));
  }

  fetchBreakdownReasonsAndSolution(
    factorySerialNumber: TREQUEST_CREATE_TICKET['machineFactorySerialNumber'],
    machineId: TM8MachineId,
  ) {
    this.store.dispatch(new TicketManagementActions.GetBreakdownReasonAndSolutions(factorySerialNumber, machineId));
  }

  fetchFactoryShiftRevisionDetails(payload: Omit<TREQUEST_GET_SHIFT_DETAILS, 'factoryId'>) {
    this.store.dispatch(new TicketManagementActions.GetFactoryShiftRevisionDetails(payload));
  }
  async addShiftRevision(payload: TREQUEST_ADD_REVISION): Promise<boolean> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);

    return lastValueFrom(
      this.mmDataService.addShiftRevisions(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async getLocationShifts(payload: TREQUEST_GET_LOCATION_SHIFTS) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);

    return lastValueFrom(
      this.mmDataService.getLocationShifts(payload).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  updateShiftRevision(
    payload: Omit<TREQUEST_UPDATE_REVISION, 'factoryId'>,
    getFactoryShiftPayload: Omit<TREQUEST_GET_SHIFT_DETAILS, 'factoryId'>,
  ) {
    this.store.dispatch(new TicketManagementActions.UpdateShiftRevision(payload, getFactoryShiftPayload));
  }

  deleteShiftRevision(
    shiftRevisionId: TShiftRevision['shiftRevisionId'],
    getFactoryShiftPayload: Omit<TREQUEST_GET_SHIFT_DETAILS, 'factoryId'>,
  ) {
    this.store.dispatch(new TicketManagementActions.DeleteShiftRevision(shiftRevisionId, getFactoryShiftPayload));
  }

  fetchAllMaintenanceTickets(
    payload: TREQUEST_GET_ALL_MAINTENANCE_TICKETS,
    fetchReason: TMaintenanceTicketsFetchReason,
  ) {
    this.store.dispatch(new TicketManagementActions.GetAllMaintenanceTickets(payload, fetchReason));
  }

  createMaintenanceTicket(payload: TREQUEST_CREATE_MAINTENANCE_TICKET) {
    this.store.dispatch(new TicketManagementActions.CreateMaintenanceTicket(payload));
  }

  startMaintenanceTicket(payload: TREQUEST_START_MAINTENANCE_TICKET) {
    this.store.dispatch(new TicketManagementActions.StartMaintenanceTicket(payload));
  }

  completeMaintenanceTicket(payload: TREQUEST_COMPLETE_MAINTENANCE_TICKET) {
    this.store.dispatch(new TicketManagementActions.CompleteMaintenanceTicket(payload));
  }

  reAssignMaintenanceMechanic(
    payload: TREQUEST_REASSIGN_MAINTENANCE_MECHANIC,
    currentTicketStatus: TMaintenanceTicketStatus,
  ) {
    this.store.dispatch(new TicketManagementActions.ReAssignMaintenanceMechanic(payload, currentTicketStatus));
  }

  searchSparepartNames(searchStr: string) {
    return from(this.appNotificationUtilClass.presentLoading('Loading', 3000)).pipe(
      switchMap((loading) =>
        this.mmDataService.searchSparepartNames(searchStr).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loading);
          }),
        ),
      ),
    );
  }

  async getAllBrandsAndSpecificationsByName(payload: TREQUEST_GET_ALL_BRANDS_AND_SPECIFICATIONS) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);

    return lastValueFrom(
      this.mmDataService.getAllBrandsAndSpecificationsByName(payload).pipe(
        map((res) => res.brandsWithSpecifications),
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async createSparepartOrNeedleRequest(payload: TREQUEST_RAISE_SPAREPART_REQUEST | TREQUEST_RAISE_NEEDLE_REQUEST) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);

    return lastValueFrom(
      this.mmDataService.createSparepartRequest(payload).pipe(
        map(() => {
          this.appNotificationUtilClass.presentToastMM('Request successfully raised');
          return true;
        }),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  resetSparepartRequestDetails() {
    this.store.dispatch(new TicketManagementActions.ResetSparepartRequestDetails());
  }

  fetchSparepartRequestBySource(payload: TREQUEST_GET_SPAREPART_REQUEST_BY_SOURCE) {
    this.store.dispatch(new TicketManagementActions.GetSparepartRequestBySource(payload));
  }

  cancelSparepartRequest(requestId: TRaiseARequestId) {
    this.store.dispatch(new TicketManagementActions.CancelSparepartRequest(requestId));
  }

  fetchSparepartRequestReasons(
    sparePartType: TRESPONSE_GET_SPAREPART_REQUEST_BY_SOURCE['requests'][number]['sparePartType'],
  ) {
    this.store.dispatch(new TicketManagementActions.GetSparepartRequestReasons(sparePartType));
  }

  markChecklistCompleted(
    payload: TREQUEST_MARK_CHECKLIST_COMPLETED,
    ticketStatus: TREQUEST_GET_ALL_MAINTENANCE_TICKETS['maintenanceTicketStatus'],
  ) {
    this.store.dispatch(new TicketManagementActions.MarkChecklistCompleted(payload, ticketStatus));
  }

  getEmployeeTicketSummary() {
    return this.store.pipe(select(TicketManagementSelectors.getEmployeeTicketSummary));
  }

  getTicketSummaryAnalytics() {
    return this.store.pipe(select(TicketManagementSelectors.getTicketSummaryAnalytics));
  }

  getEmployeeTickets() {
    return this.store.pipe(select(TicketManagementSelectors.getEmployeeTickets));
  }

  getEmployeeTicketDetails() {
    return this.store.pipe(select(TicketManagementSelectors.getEmployeeTicketDetails));
  }

  getMachineTicketHistory() {
    return this.store.pipe(select(TicketManagementSelectors.getMachineTicketHistory));
  }

  getBreakdownReasonsAndSolution() {
    return this.store.pipe(select(TicketManagementSelectors.getBreakdownReasonsAndSolution));
  }

  getEmployeeRole() {
    return this.store.pipe(select(TicketManagementSelectors.getEmployeeRole));
  }

  getFactoryShiftRevisionDetails() {
    return this.store.pipe(select(TicketManagementSelectors.getFactoryShiftRevisionDetails));
  }

  getMaintenanceTickets() {
    return this.store.pipe(select(TicketManagementSelectors.getMaintenanceTickets));
  }

  getSparepartRequestBySource() {
    return this.store.pipe(select(TicketManagementSelectors.getSparePartRequestsBySource));
  }

  getSparepartRequestReasons() {
    return this.store.pipe(select(TicketManagementSelectors.getSparepartRequestReasons));
  }

  getEmployeeTicketsPagination() {
    return this.store.pipe(select(TicketManagementSelectors.getEmployeeTicketsPagination));
  }

  setEmployeeTicketsPagination(option: TUserPaginationOption) {
    return this.store.dispatch(new TicketManagementActions.SetEmployeeTicketsPagination(option));
  }

  getMaintenanceTicketsPagination() {
    return this.store.pipe(select(TicketManagementSelectors.getMaintenanceTicketsPagination));
  }

  setMaintenanceTicketsPagination(option: TUserPaginationOption) {
    return this.store.dispatch(new TicketManagementActions.SetMaintenanceTicketsPagination(option));
  }
}

export type TGetEmployeeTicketSummary = ReturnType<TicketManagementFacade['getEmployeeTicketSummary']>;
export type TGetTicketSummaryAnalytics = ReturnType<TicketManagementFacade['getTicketSummaryAnalytics']>;
export type TGetEmployeeTickets = ReturnType<TicketManagementFacade['getEmployeeTickets']>;
export type TGetEmployeeTicketDetails = ReturnType<TicketManagementFacade['getEmployeeTicketDetails']>;
export type TGetMachineTicketHistory = ReturnType<TicketManagementFacade['getMachineTicketHistory']>;
export type TGetBreakdownReasonsAndSolution = ReturnType<TicketManagementFacade['getBreakdownReasonsAndSolution']>;
export type TGetEmployeeRole = ReturnType<TicketManagementFacade['getEmployeeRole']>;
export type TGetFactoryShiftRevisionDetails = ReturnType<TicketManagementFacade['getFactoryShiftRevisionDetails']>;
export type TGetMaintenanceTickets = ReturnType<TicketManagementFacade['getMaintenanceTickets']>;
export type TGetSparepartRequestBySource = ReturnType<TicketManagementFacade['getSparepartRequestBySource']>;
export type TGetSparepartRequestReasons = ReturnType<TicketManagementFacade['getSparepartRequestReasons']>;
